/**
 * Merchant Pro V1 PR-2 — Brand-Owner Dashboard
 *
 * IN-PLACE REFACTOR of the previous OverviewPage. The route remains
 * `/owner` (sidebar label renamed Overview → Dashboard) and the page
 * filename + symbol name (`OverviewPage`) are preserved so existing
 * imports (App.js + tests) keep working. The historical analytics
 * panel (`<BrandAnalyticsPanel>`) has moved to its own /owner/analytics
 * route — this Dashboard is now reservation-centric per the locked
 * reference pack (Image 2 — Merchant Pro desktop layout §1
 * "DASHBOARD").
 *
 * REUSE discipline (per PR-2 Review Package — Top Reuse Opportunities):
 *   • Reuses <PageHeader> for greeting (kicker / title / subtitle).
 *   • Reuses <SectionCard> for every panel container.
 *   • Reuses <KpiCard> + `kpi-{name}` testID naming convention.
 *   • Reuses <StatusPill> for reservation status badges (no parallel
 *     pill component built).
 *   • Reuses <FullSpinner> + <QueryError> for loading / error states.
 *   • Uses /api/owner/dashboard/today aggregator (single round-trip;
 *     PR-2 backend addition V1-A2).
 *
 * Operator-locked: only Confirm / Check-in / Cancel actions visible in
 * PR-2; the Mark No-Show / Complete / Notes paths are deferred and
 * physically absent from the JSX.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  Footprints,
  Users,
  AlertTriangle,
  Clock,
  ArrowRight,
  Activity,
  Gauge,
} from 'lucide-react';

import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';

import {
  PageHeader,
  SectionCard,
  StatusPill,
  FullSpinner,
  QueryError,
} from '@/components/ui_primitives/Misc';
import KpiCard from '@/components/ui_primitives/KpiCard';

/* ───────────────────────── helpers ───────────────────────── */

function timeShort(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function deltaProps(value) {
  if (value == null) return undefined;
  if (value === 0) {
    return { direction: 'flat', label: '0', subtitle: 'vs yesterday' };
  }
  if (value > 0) {
    return { direction: 'up', label: `+${value}`, subtitle: 'vs yesterday' };
  }
  return { direction: 'down', label: `${value}`, subtitle: 'vs yesterday' };
}

/* StatusPill variant mapping — REUSE the 5 existing variants. */
const STATUS_PILL_VARIANT = {
  requested: 'amber',
  held_pending: 'amber',
  waitlist_promotion_pending: 'amber',
  confirmed: 'gold',
  checked_in: 'blue',
  completed: 'green',
  cancelled_by_owner: 'red',
  cancelled_by_customer: 'red',
  cancelled_by_cashier: 'red',
  cancelled_by_system: 'red',
  no_show: 'red',
  rescheduled: 'slate',
  expired: 'slate',
};

function reservationStatusPill(status) {
  const variant = STATUS_PILL_VARIANT[status] || 'slate';
  const label = (status || '').replace(/_/g, ' ');
  return <StatusPill variant={variant}>{label}</StatusPill>;
}

function SectionHeading({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-4 gap-4">
      <div>
        <div className="font-display text-lg font-semibold text-white">
          {title}
        </div>
        {subtitle ? (
          <div className="text-xs text-white/55 mt-0.5">{subtitle}</div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

/* PR-4c — Time-of-day greeting (4c-AC#1). */
function getTimeOfDayKicker() {
  const h = new Date().getHours();
  if (h < 5)  return 'Late Night';
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  if (h < 21) return 'Good Evening';
  return 'Late Night';
}

/* ───────────────────────── main page ───────────────────────── */

export default function OverviewPage() {
  const token = useAuthStore((s) => s.token);

  const dashboardQ = useQuery({
    queryKey: qk.ownerDashboardToday(),
    queryFn: () => api.ownerDashboardToday(token),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled: !!token,
  });

  if (dashboardQ.isLoading) return <FullSpinner />;
  if (dashboardQ.isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          kicker="Dashboard"
          title="Dashboard"
          subtitle="Today's reservations, walk-ins, waitlist, and no-shows."
        />
        <QueryError error={dashboardQ.error} onRetry={dashboardQ.refetch} />
      </div>
    );
  }

  const data = dashboardQ.data || {};
  const kpis = data.kpis || {};
  const vsy = kpis.vs_yesterday || {};
  const glance = data.today_at_a_glance || {};
  const upcoming = data.upcoming_reservations || [];
  const waitlist = data.waitlist_now || [];
  const todayLabel = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  return (
    <div className="space-y-6" data-testid="owner-dashboard-page">
      {/* PR-4c — Premium hero banner (4c-AC#1). */}
      <div
        data-testid="dashboard-hero"
        className="relative overflow-hidden rounded-3xl p-7 sm:p-9"
        style={{
          background:
            'linear-gradient(135deg, rgba(212,175,55,0.20) 0%, rgba(212,175,55,0.06) 35%, rgba(21,21,27,0.95) 75%)',
          border: '1px solid rgba(212,175,55,0.30)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        <div className="absolute inset-y-0 right-0 w-1/3 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at right center, rgba(212,175,55,0.18), transparent 65%)',
          }}
        />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold/85">
            {getTimeOfDayKicker()} · {todayLabel}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-2 tracking-tight">
            Hello, {data.brand_name || 'Admin'}{' '}
            <span aria-hidden>👋</span>
          </h1>
          <p className="text-sm text-white/70 mt-2 max-w-2xl">
            Today at a glance — reservations, walk-ins, waitlist, and no-shows
            across {data.branch_count != null ? `${data.branch_count} branches` : 'your branches'}.
          </p>
        </div>
      </div>

      {/* 4 KPI tiles — reuse <KpiCard> + kpi-{name} testID convention */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testID="kpi-todays-reservations"
          label="Today's Reservations"
          value={kpis.todays_reservations ?? 0}
          icon={<CalendarDays size={16} />}
          tone="gold"
          delta={deltaProps(vsy.todays_reservations)}
        />
        <KpiCard
          testID="kpi-walkin-checked-in"
          label="Walk-in Checked In"
          value={kpis.walkin_checked_in ?? 0}
          icon={<Footprints size={16} />}
          tone="green"
          delta={deltaProps(vsy.walkin_checked_in)}
        />
        <KpiCard
          testID="kpi-waitlist-now"
          label="Waitlist Now"
          value={kpis.waitlist_now ?? 0}
          icon={<Users size={16} />}
          tone="slate"
        />
        <KpiCard
          testID="kpi-no-shows-today"
          label="No-Shows Today"
          value={kpis.no_shows_today ?? 0}
          icon={<AlertTriangle size={16} />}
          tone="red"
          delta={deltaProps(vsy.no_shows_today)}
        />
      </div>

      {/* Phase V1.1-WP5 — Session Operations KPI row. Two tiles render
          alongside the canonical Today row: "Active Sessions" (count of
          `reservations.session_state == "active"`, brand+branch scoped,
          live via `kpis.active_sessions_count`) and "Utilization" (which
          maps to the existing package §8 real-time occupancy formula
          surfaced as `today_at_a_glance.occupancy_pct`). Both reuse the
          canonical <KpiCard> primitive — NO new tile component. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testID="kpi-active-sessions"
          label="Active Sessions"
          value={kpis.active_sessions_count ?? 0}
          icon={<Activity size={16} />}
          tone="purple"
        />
        <KpiCard
          testID="kpi-utilization"
          label="Utilization"
          value={`${glance.occupancy_pct ?? 0}%`}
          icon={<Gauge size={16} />}
          tone="blue"
        />
      </div>

      {/* Today at a Glance */}
      <SectionCard testID="dashboard-today-at-a-glance">
        <SectionHeading
          title="Today at a Glance"
          subtitle="Live snapshot of occupancy, party size, and tables in use."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <GlanceRow
            label="Occupancy"
            primary={`${glance.occupancy_pct ?? 0}%`}
            sub="of active tables in use"
            progressPct={glance.occupancy_pct}
            testID="glance-occupancy"
          />
          <GlanceRow
            label="Tables Occupied"
            primary={glance.tables_occupied ?? 0}
            sub="across all branches"
            testID="glance-tables-occupied"
          />
          <GlanceRow
            label="Avg Party Size"
            primary={(glance.avg_party_size ?? 0).toFixed(1)}
            sub="today's confirmed reservations"
            testID="glance-avg-party-size"
          />
        </div>
      </SectionCard>

      {/* Upcoming Reservations + Waitlist Now */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard testID="dashboard-upcoming-reservations">
          <SectionHeading
            title="Upcoming Reservations"
            subtitle="Next confirmed bookings today."
            action={
              <Link
                to="/owner/reservations"
                className="text-xs text-gold/85 hover:text-gold inline-flex items-center gap-1"
                data-testid="upcoming-view-all-link"
              >
                View all <ArrowRight size={12} />
              </Link>
            }
          />
          {upcoming.length === 0 ? (
            <EmptyRow label="No confirmed reservations remaining today." />
          ) : (
            <ul className="divide-y divide-white/5">
              {upcoming.slice(0, 3).map((res) => (
                <li
                  key={res.id}
                  className="py-3 flex items-start gap-3"
                  data-testid={`upcoming-row-${res.id}`}
                >
                  <div className="mt-1 w-9 h-9 rounded-lg bg-gold/10 border border-gold/20 grid place-items-center text-gold">
                    <CalendarDays size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium text-white/90 truncate">
                        {res.customer?.name || 'Walk-in / unknown'}
                      </div>
                      {reservationStatusPill(res.status)}
                    </div>
                    <div className="text-xs text-white/55 mt-0.5">
                      {timeShort(res.slot_start)} · Party {res.party_size}
                      {res.booking_code ? (
                        <span className="ml-2 text-gold/70">
                          · {res.booking_code}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <Link
                    to={`/owner/reservations/${res.id}`}
                    className="text-[11px] text-gold/85 hover:text-gold whitespace-nowrap self-center"
                    data-testid={`upcoming-link-${res.id}`}
                  >
                    Open
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard testID="dashboard-waitlist-now">
          <SectionHeading
            title="Waitlist Now"
            subtitle="Active waitlist entries across branches."
            action={
              <span
                className="text-xs text-white/40"
                data-testid="waitlist-view-all-stub"
              >
                View all coming soon
              </span>
            }
          />
          {waitlist.length === 0 ? (
            <EmptyRow label="Waitlist is empty right now." />
          ) : (
            <ul className="divide-y divide-white/5">
              {waitlist.slice(0, 3).map((entry) => (
                <li
                  key={entry.id}
                  className="py-3 flex items-start gap-3"
                  data-testid={`waitlist-row-${entry.id}`}
                >
                  <div className="mt-1 w-9 h-9 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-white/70">
                    <Clock size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium text-white/90 truncate">
                        Party of {entry.party_size}
                      </div>
                      <StatusPill
                        variant={entry.status === 'notified' ? 'gold' : 'amber'}
                      >
                        {entry.status}
                      </StatusPill>
                    </div>
                    <div className="text-xs text-white/55 mt-0.5">
                      Position #{entry.position_in_queue ?? '—'}
                      {entry.estimated_wait_min != null ? (
                        <span className="ml-2">
                          · ~{entry.estimated_wait_min} min wait
                        </span>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

/* ───────────────── inline subcomponents ───────────────── */

function GlanceRow({ label, primary, sub, progressPct, testID }) {
  return (
    <div data-testid={testID} className="space-y-1">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-white/50">
        {label}
      </div>
      <div className="text-2xl font-display font-bold tracking-tight">
        {primary}
      </div>
      {sub ? <div className="text-xs text-white/45">{sub}</div> : null}
      {progressPct != null ? (
        <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full bg-gold/80"
            style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

function EmptyRow({ label }) {
  return (
    <div className="py-6 text-center text-sm text-white/45">{label}</div>
  );
}
